import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import {
  deleteAccount as deleteAccountRequest,
  findAccounts,
  saveAccount,
} from "../services/accounting";
import { ellipsis } from "../utils";
import {
  showErrorNotification,
  showSuccessNotification,
} from "./notifications";

export const ACCOUNT_FEATURES = {
  SYSTEM_LOCKED: 1,
  INACTIVE: 2,
  NO_POSTING: 4,
  CONTRA_ACCOUNT: 8,
};

export const ACCOUNT_FEATURES_NAMES = {
  1: "System Locked",
  2: "Inactive",
  4: "No Posting",
  8: "Contra Account",
};

// export const getSupportedFeatures = (features) => {
//   return Object.keys(ACCOUNT_FEATURES).filter((key) => {
//     const flagValue = ACCOUNT_FEATURES[key];
//     return hasFeature(features, flagValue)
//   });
// };

export const hasFeature = (features, featureFlag) => !!(features & featureFlag);

export const ACCOUNT_TYPES = {
  1: "Assets",
  2: "Liabilities",
  3: "Equity",
  4: "Revenue",
  5: "Expenses",
};

export const ACCOUNT_SUBTYPES = {
  101: { id: 101, name: "Cash/Bank", type: 1 },
  102: { id: 102, name: "Receivables (AR)", type: 1 },
  103: { id: 103, name: "Inventory", type: 1 },
  104: { id: 104, name: "Fixed Assets", type: 1 },
  105: { id: 105, name: "Other Current", type: 1 },
  201: { id: 201, name: "Payables (AP)", type: 2 },
  202: { id: 202, name: "Credit Cards", type: 2 },
  203: { id: 203, name: "Current Debt", type: 2 },
  204: { id: 204, name: "Long-term Debt", type: 2 },
  301: { id: 301, name: "Equity", type: 3 },
  302: { id: 302, name: "Retained Earnings", type: 3 },
  401: { id: 401, name: "Operating Revenue", type: 4 },
  402: { id: 402, name: "Other Revenue", type: 4 },
  501: { id: 501, name: "Cost of Sales", type: 5 },
  502: { id: 502, name: "Operating Expenses", type: 5 },
  503: { id: 503, name: "Other Expenses", type: 5 },
};

export const getAccountTypeName = (subtype) =>
  ACCOUNT_TYPES[ACCOUNT_SUBTYPES[subtype]?.type];

const coaAdapter = createEntityAdapter({
  sortComparer: (a, b) =>
    `${a.code || a.name || ""}`.localeCompare(`${b.code || b.name || ""}`),
});

const initialStateEdit = {
  id: null,
  isSaving: false,
};

const initialState = {
  ...coaAdapter.getInitialState(),
  edit: initialStateEdit,
};

const coaSlice = createSlice({
  name: "coa",
  initialState,
  reducers: {
    cancelEdit: (state) => ({ ...state, edit: initialStateEdit }),
    edit: (state, { payload }) => {
      state.edit.id = payload.id;
    },
    setAccounts: coaAdapter.setAll,
  },
  extraReducers: (builder) => {
    builder
      .addCase(save.fulfilled, (state, { payload }) =>
        coaAdapter.setOne({ ...state, edit: initialStateEdit }, payload),
      )
      .addCase(save.pending, (state) => {
        state.edit.isSaving = true;
      })
      .addCase(save.rejected, (state) => {
        state.edit.isSaving = false;
      })
      .addCase(deleteAccount.pending, (state, { meta }) =>
        coaAdapter.updateOne(state, {
          id: meta.arg,
          changes: { deleteInProgress: true },
        }),
      )
      .addCase(deleteAccount.rejected, (state, { meta }) =>
        coaAdapter.updateOne(state, {
          id: meta.arg,
          changes: { deleteInProgress: false },
        }),
      )
      .addCase(deleteAccount.fulfilled, (state, { payload: accountId }) =>
        coaAdapter.removeOne(state, accountId),
      );
  },
});

export default coaSlice.reducer;

export const { cancelEdit, edit, setAccounts } = coaSlice.actions;

export const isValidAccount = (account) =>
  !!account?.code && !!account?.name && !!account?.type;

export const selectors = coaAdapter.getSelectors((state) => state.coa);

export const selectAccountRelations = createSelector(
  selectors.selectIds,
  selectors.selectEntities,
  (ids, entities) =>
    ids.reduce(
      (relations, id) =>
        (({ parentId, type: subtype }) =>
          ((type) => ({
            ...relations,
            ...(parentId
              ? { [parentId]: [...(relations[parentId] || []), id] }
              : { [type]: [...(relations[type] || []), id] }),
          }))(ACCOUNT_SUBTYPES[subtype]?.type))(entities[id]),
      {},
    ),
);

export const selectIsParentAccount = createSelector(
  (_, parentId) => parentId,
  selectAccountRelations,
  (parentId, relations) => !!relations[parentId]?.length,
);

export const selectEditedAccountId = (state) => state.coa.edit.id;

export const selectAccountEditOpen = (state) => !!state.coa.edit.id;

export const selectAccountIsSaving = (state) => state.coa.edit.isSaving;

export const selectCandidateParentAccounts = createSelector(
  (_, type) => type,
  (_, __, parentId) => parentId,
  selectors.selectAll,
  selectAccountRelations,
  (type, parentId, accounts, relations) => {
    if (type < 100) {
      return [];
    }
    const minType = type - (type % 100);
    const maxType = minType + 99;
    const children = relations[parentId] || [];
    return accounts.filter(
      (acc) =>
        acc.features & 4 &&
        minType <= acc.type &&
        acc.type <= maxType &&
        acc.id !== parentId &&
        !children.includes(acc.id),
    );
  },
);

export const selectCanDeleteAccount = createSelector(
  selectors.selectById,
  selectAccountRelations,
  (account, relations) => {
    const isParent = !!relations[account?.id]?.length;
    const isSystemLocked = hasFeature(
      account?.features,
      ACCOUNT_FEATURES.SYSTEM_LOCKED,
    );
    return account && !isSystemLocked && !isParent;
  },
);

export const loadChartOfAccounts = createAsyncThunk(
  "coa/loadAll",
  (_, { dispatch }) =>
    findAccounts().then((accounts) => {
      dispatch(setAccounts(accounts));
    }),
);

const accLabel = (acc) => ellipsis(32)(`${acc?.code} - ${acc?.name}`);

export const save = createAsyncThunk(
  "coa/save",
  (account, { dispatch, getState }) => {
    const id = selectEditedAccountId(getState());
    const payload = {
      ...account,
      id,
    };
    return saveAccount(payload)
      .then((savedAccount) => {
        dispatch(
          showSuccessNotification(
            `Account ${accLabel(savedAccount)} saved successfully`,
          ),
        );
        return savedAccount;
      })
      .catch((err) => {
        dispatch(showErrorNotification(err.message));
        throw err;
      });
  },
);

export const deleteAccount = createAsyncThunk(
  "coa/deleteAccount",
  (accountId, { dispatch, getState }) => {
    const accToDelete = selectors.selectById(getState(), accountId);
    return deleteAccountRequest(accountId)
      .then(() => {
        dispatch(
          showSuccessNotification(`Account ${accLabel(accToDelete)} deleted`),
        );
        return accountId;
      })
      .catch((err) => {
        dispatch(showErrorNotification(err.message));
        throw err;
      });
  },
);
